import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { styled, Tab, Tabs, useTheme } from '@mui/material';
import { TableV1 } from '../../../../components/Table/TableV1';
import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router';
import { TableSkeletonLoader } from '../../../../components/SkeletonLoader/Table.SkeletonLoader';
import { SingleTimesheetHeader } from './SingleTimesheetHeader';
import { useMonthlySingleTimesheet } from '../../../../queries/hooks/useMonthlySingleTimesheet';
import { useDealOwnersSingleTableColumns } from '../../hooks/useDealOwnersSingleTableColumns';

import { isSmallScreenState, userState } from '../../../../state/UIState';
import { WeekTable } from '../../../AccountantSingleTimesheet/components/AccountantSingleTimesheetWeekTable';

export function DealOwnerSingleTimesheetTable() {
  const { colors } = useTheme();
  const { id } = useParams();
  const user = useAtomValue(userState);
  const [selectedTab, setSelectedTab] = useState('month');
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const { data: singleTimesheet, isLoading: dataIsLoading } = useMonthlySingleTimesheet({
    id: Number(id),
  });

  // Show the rows THIS deal owner is responsible for. The backend attaches the EFFECTIVE deal-owner
  // approvers to each row (`row.dealOwners`) with approval delegations applied, so a delegate sees the
  // delegated deal and the original owner no longer does. Fall back to the raw owned-deals set only if the
  // backend didn't provide effective approvers (older cached payloads), preserving prior behaviour.
  const dealOwnerDeals = useMemo(() => {
    return singleTimesheet?.timesheetRows.filter((row) =>
      row.dealOwners
        ? row.dealOwners.some((approver) => approver.id === user?.id)
        : user?.dealOwnerDeals?.some((deal) => deal.id === row.dealId)
    );
  }, [singleTimesheet?.timesheetRows, user?.dealOwnerDeals, user?.id]);

  const columns = useDealOwnersSingleTableColumns();

  const table = useReactTable({
    data: dealOwnerDeals ?? [],
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (item) => String(item.id),
  });

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  if (dataIsLoading || !singleTimesheet) return <TableSkeletonLoader />;

  return (
    <>
      <SingleTimesheetHeader />
      <Tabs value={selectedTab} onChange={handleTabChange}>
        <Tab
          label='By Month'
          value={'month'}
          sx={{
            color: colors.text.main,
            textTransform: 'none',
          }}
        />
        <Tab
          label='By Week'
          value={'week'}
          sx={{
            color: colors.text.main,
            textTransform: 'none',
          }}
        />
      </Tabs>
      {selectedTab === 'month' && (
        <TableV1
          hasFooter
          table={table}
          height='auto'
          wrapperStyle={{
            maxHeight: 'calc(100vh - 288px)',
            overflowX: 'scroll',
            border: `1px solid ${colors.border.default}`,
          }}
        />
      )}
      {selectedTab === 'week' && (
        <Wrapper isSmallScreen={isSmallScreen}>
          {singleTimesheet.weeklyTimesheets.map((timesheet, index) => (
            <WeekTable
              tableData={timesheet.timesheetRows}
              index={index}
              key={`table-${index}`}
              holidays={timesheet.holidays}
              weekDates={timesheet.weekDates}
              weekStart={timesheet.weekStart}
            />
          ))}
        </Wrapper>
      )}
    </>
  );
}

const Wrapper = styled('div')<{ isSmallScreen: boolean }>`
  height: ${({ isSmallScreen }) => (isSmallScreen ? '50vh' : 'auto')};
  overflow-y: ${({ isSmallScreen }) => (isSmallScreen ? 'scroll' : 'none')};
`;